import { useState, useEffect, useRef, useCallback } from 'react';
import {
    ArrowLeft, ChevronLeft, ChevronRight, Loader2, Check, X,
    FileText, Video, FileDown, Clock, CheckCircle2, XCircle, Award, Play, RotateCcw, Eye,
    BookOpen, ClipboardCheck
} from 'lucide-react';
import { nactiPdfjs } from '../lib/pdfjs';

const SEKCE_TYP_ICONS = { obsah: FileText, video: Video, soubor: FileDown };

const token = () => document.querySelector('meta[name="csrf-token"]')?.content;
const apiPost = async (url, body) => {
    const resp = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': token() },
        body: body ? JSON.stringify(body) : undefined,
    });
    return resp.json();
};

// ── PDF Viewer s vlastním pagingem ──────────────────────────
export function PdfViewer({ url, nazev, onLastPage }) {
    const canvasRef = useRef(null);
    const containerRef = useRef(null);
    const [pdfDoc, setPdfDoc] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [rendering, setRendering] = useState(false);
    const renderTaskRef = useRef(null);

    useEffect(() => {
        let cancelled = false;
        const loadPdf = async () => {
            const pdfjsLib = await nactiPdfjs();
            try {
                const doc = await pdfjsLib.getDocument(url).promise;
                if (!cancelled) {
                    setPdfDoc(doc);
                    setTotalPages(doc.numPages);
                    if (doc.numPages <= 1 && onLastPage) onLastPage();
                }
            } catch (err) {
                console.error('PDF load failed:', err);
            }
        };
        loadPdf();
        return () => { cancelled = true; };
    }, [url]);

    // Render current page — fit to container width AND available viewport height
    useEffect(() => {
        if (!pdfDoc || !canvasRef.current) return;
        let cancelled = false;
        setRendering(true);

        const render = async () => {
            try {
                const page = await pdfDoc.getPage(currentPage);
                const canvas = canvasRef.current;
                if (!canvas || cancelled) return;
                const ctx = canvas.getContext('2d');
                const containerWidth = containerRef.current?.clientWidth || canvas.parentElement?.clientWidth || 600;
                // Reserve space for paging controls (~100px) + header/nav (~200px)
                const availableHeight = window.innerHeight - 300;
                const baseViewport = page.getViewport({ scale: 1 });
                const scaleByWidth = containerWidth / baseViewport.width;
                const scaleByHeight = availableHeight / baseViewport.height;
                const scale = Math.min(scaleByWidth, scaleByHeight);
                const viewport = page.getViewport({ scale });

                canvas.width = viewport.width;
                canvas.height = viewport.height;

                if (renderTaskRef.current) {
                    try { renderTaskRef.current.cancel(); } catch {}
                }

                const task = page.render({ canvasContext: ctx, viewport });
                renderTaskRef.current = task;
                await task.promise;
            } catch (err) {
                if (err?.name !== 'RenderingCancelledException') console.error(err);
            }
            if (!cancelled) setRendering(false);
        };
        render();
        return () => { cancelled = true; };
    }, [pdfDoc, currentPage]);

    if (!pdfDoc) {
        return (
            <div className="flex items-center justify-center py-12" style={{ color: 'var(--text-muted)' }}>
                <Loader2 size={20} className="animate-spin" />
                <span className="ml-2 text-sm">Načítám dokument...</span>
            </div>
        );
    }

    return (
        <div ref={containerRef}>
            {/* PDF canvas */}
            <div style={{
                borderRadius: 6, overflow: 'hidden', border: '1px solid var(--border-default)',
                position: 'relative', display: 'flex', justifyContent: 'center',
                background: 'var(--surface-elevated)',
            }}>
                <canvas ref={canvasRef} style={{ display: 'block', maxWidth: '100%' }} />
                {rendering && (
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.1)' }}>
                        <Loader2 size={20} className="animate-spin" style={{ color: 'var(--brand)' }} />
                    </div>
                )}
            </div>

            {/* Paging controls */}
            {totalPages > 1 && (
                <div className="flex items-center justify-center gap-4 mt-3">
                    <button
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage <= 1}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium"
                        style={{
                            background: 'var(--surface-elevated)', border: '1px solid var(--border-default)',
                            color: currentPage <= 1 ? 'var(--text-muted)' : 'var(--text-primary)',
                            cursor: currentPage <= 1 ? 'not-allowed' : 'pointer', opacity: currentPage <= 1 ? 0.5 : 1,
                        }}
                    >
                        <ChevronLeft size={14} /> Předchozí
                    </button>
                    <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                        {currentPage} / {totalPages}
                    </span>
                    <button
                        onClick={() => {
                            const next = Math.min(totalPages, currentPage + 1);
                            setCurrentPage(next);
                            if (next === totalPages && onLastPage) onLastPage();
                        }}
                        disabled={currentPage >= totalPages}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium"
                        style={{
                            background: 'var(--surface-elevated)', border: '1px solid var(--border-default)',
                            color: currentPage >= totalPages ? 'var(--text-muted)' : 'var(--text-primary)',
                            cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer', opacity: currentPage >= totalPages ? 0.5 : 1,
                        }}
                    >
                        Další <ChevronRight size={14} />
                    </button>
                </div>
            )}

            {/* Download link */}
            <div className="flex justify-center mt-2">
                <a
                    href={url}
                    download={nazev}
                    className="text-xs"
                    style={{ color: 'var(--text-muted)', textDecoration: 'underline' }}
                >
                    Stáhnout PDF
                </a>
            </div>
        </div>
    );
}

export default function MojeSkoleniDetail({ id, onNavigate }) {
    const [prubeh, setPrubeh] = useState(null);
    const [loading, setLoading] = useState(true);
    const [currentSekce, setCurrentSekce] = useState(0);
    const [showKviz, setShowKviz] = useState(false);
    const [kvizOdpovedi, setKvizOdpovedi] = useState({});
    const [kvizVysledek, setKvizVysledek] = useState(null);
    const [submittingKviz, setSubmittingKviz] = useState(false);
    const [showComplete, setShowComplete] = useState(false);
    const [showIntro, setShowIntro] = useState(false);
    const [starting, setStarting] = useState(false);
    const [pdfRead, setPdfRead] = useState({}); // { [sekceId]: true }
    const [videoWatched, setVideoWatched] = useState({}); // { [sekceId]: true }
    const timeRef = useRef(null);
    const currentSekceRef = useRef(0);

    const fetchData = useCallback(() => {
        fetch(`/api/moje-skoleni/${id}`).then(r => r.json()).then(data => {
            setPrubeh(data);
            setLoading(false);
            // Show intro for new trainings, go to content for in-progress
            if (data.stav === 'prirazeno') {
                setShowIntro(true);
            } else if (data.skoleni?.sekce) {
                const doneSekceIds = new Set(data.prubeh_sekce?.filter(ps => ps.dokonceno).map(ps => ps.sekce_id) || []);
                const firstIncomplete = data.skoleni.sekce.findIndex(s => !doneSekceIds.has(s.id));
                if (firstIncomplete >= 0) setCurrentSekce(firstIncomplete);
            }
        });
    }, [id]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const startTraining = async () => {
        setStarting(true);
        await apiPost(`/api/moje-skoleni/${id}/zahajit`);
        setPrubeh(prev => ({ ...prev, stav: 'zahajeno' }));
        setShowIntro(false);
        setStarting(false);
    };

    // Time tracking heartbeat (every 30s)
    useEffect(() => {
        if (!prubeh || prubeh.stav !== 'zahajeno' || !prubeh.skoleni?.sekce?.length) return;

        const interval = setInterval(() => {
            const sekce = prubeh.skoleni.sekce[currentSekceRef.current];
            if (sekce) {
                apiPost(`/api/moje-skoleni/${id}/sekce/${sekce.id}/cas`, { delta_s: 30 });
            }
        }, 30000);

        return () => clearInterval(interval);
    }, [prubeh?.stav, id, prubeh?.skoleni?.sekce?.length]);

    // Keep ref in sync
    useEffect(() => { currentSekceRef.current = currentSekce; }, [currentSekce]);

    if (loading || !prubeh) {
        return <div className="flex items-center justify-center" style={{ minHeight: 300, color: 'var(--text-muted)' }}><Loader2 size={24} className="animate-spin" /></div>;
    }

    const skoleni = prubeh.skoleni;
    const sekce = skoleni.sekce || [];
    const doneSekceIds = new Set(prubeh.prubeh_sekce?.filter(ps => ps.dokonceno).map(ps => ps.sekce_id) || []);
    const aktivniSekce = sekce[currentSekce];
    const isLastSekce = currentSekce === sekce.length - 1;
    const hasKviz = skoleni.typ_zakonceni !== 'skoleni';

    // Odhad času (minuty): text ~2min, video ~5min, soubor ~3min, kvíz ~1min/otázku
    const estimateMinutes = () => {
        let mins = 0;
        sekce.forEach(s => {
            if (s.typ === 'obsah') mins += 2;
            else if (s.typ === 'video') mins += 5;
            else if (s.typ === 'soubor') mins += 3;
        });
        if (hasKviz && skoleni.otazky?.length) mins += Math.ceil(skoleni.otazky.length * 1);
        return mins;
    };

    // ── Intro screen ──
    if (showIntro) {
        const estMin = estimateMinutes();
        const TYP_LABELS = { skoleni: 'Pouze školením', kviz: 'Kvízem', kviz_certifikace: 'Kvíz + Certifikace', kviz_kvalifikace: 'Kvíz + Kval. matice' };
        return (
            <div style={{ maxWidth: 600, margin: '0 auto' }} className="space-y-5 py-4">
                {/* Back */}
                <button onClick={() => onNavigate('prehled', { tab: 'rozvoj' })} className="flex items-center gap-1.5 text-sm" style={{ color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer' }}>
                    <ArrowLeft size={14} /> Zpět na osobní rozvoj
                </button>

                {/* Hero */}
                <div className="text-center space-y-3">
                    <div style={{ width: 56, height: 56, borderRadius: 10, background: 'rgba(180,122,234,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto' }}>
                        <BookOpen size={28} style={{ color: '#B47AEA' }} />
                    </div>
                    <h1 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>{skoleni.nazev}</h1>
                    {skoleni.popis && (
                        <p className="text-sm" style={{ color: 'var(--text-secondary)', maxWidth: 480, margin: '0 auto' }}>{skoleni.popis}</p>
                    )}
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-3">
                    <div className="rounded-xl p-3 text-center" style={{ background: 'var(--surface-elevated)', border: '1px solid var(--border-default)' }}>
                        <FileText size={16} style={{ color: 'var(--text-muted)', margin: '0 auto 4px' }} />
                        <p className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{sekce.length}</p>
                        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{sekce.length === 1 ? 'Sekce' : 'Sekcí'}</p>
                    </div>
                    <div className="rounded-xl p-3 text-center" style={{ background: 'var(--surface-elevated)', border: '1px solid var(--border-default)' }}>
                        <Clock size={16} style={{ color: 'var(--text-muted)', margin: '0 auto 4px' }} />
                        <p className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>~{estMin}</p>
                        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Minut</p>
                    </div>
                    <div className="rounded-xl p-3 text-center" style={{ background: 'var(--surface-elevated)', border: '1px solid var(--border-default)' }}>
                        <Award size={16} style={{ color: 'var(--text-muted)', margin: '0 auto 4px' }} />
                        <p className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{TYP_LABELS[skoleni.typ_zakonceni]?.split(' ')[0] || '—'}</p>
                        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Zakončení</p>
                    </div>
                </div>

                {/* Obsah školení */}
                <div className="rounded-xl p-4" style={{ background: 'var(--surface-elevated)', border: '1px solid var(--border-default)' }}>
                    <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Obsah školení</h3>
                    <div className="space-y-2">
                        {sekce.map((s, i) => {
                            const SIcon = SEKCE_TYP_ICONS[s.typ] || FileText;
                            return (
                                <div key={s.id} className="flex items-center gap-2.5 text-sm" style={{ color: 'var(--text-secondary)' }}>
                                    <span className="text-xs font-bold" style={{ color: 'var(--text-muted)', width: 18, textAlign: 'right' }}>{i + 1}.</span>
                                    <SIcon size={13} style={{ color: '#B47AEA', flexShrink: 0 }} />
                                    <span className="truncate">{s.nazev}</span>
                                </div>
                            );
                        })}
                        {hasKviz && (
                            <div className="flex items-center gap-2.5 text-sm" style={{ color: 'var(--text-secondary)' }}>
                                <span className="text-xs font-bold" style={{ color: 'var(--text-muted)', width: 18, textAlign: 'right' }}></span>
                                <ClipboardCheck size={13} style={{ color: '#B47AEA', flexShrink: 0 }} />
                                <span>Kvíz ({skoleni.otazky?.length || 0} otázek)</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Podmínky */}
                {hasKviz && (
                    <div className="rounded-xl p-4" style={{ background: 'var(--surface-elevated)', border: '1px solid var(--border-default)' }}>
                        <h3 className="text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Podmínky úspěšného absolvování</h3>
                        <div className="space-y-1.5 text-sm" style={{ color: 'var(--text-secondary)' }}>
                            <p>Minimální úspěšnost kvízu: <strong style={{ color: 'var(--text-primary)' }}>{skoleni.min_uspesnost_pct || 80}%</strong></p>
                            {skoleni.max_pokusu && <p>Maximální počet pokusů: <strong style={{ color: 'var(--text-primary)' }}>{skoleni.max_pokusu}</strong></p>}
                            {skoleni.typ_zakonceni === 'kviz_certifikace' && skoleni.certifikace_typ_kod && (
                                <p>Po úspěchu: obnovení certifikátu <strong style={{ color: '#10b981' }}>{skoleni.certifikace_typ_kod}</strong> na {skoleni.certifikace_platnost_mesicu || 12} měsíců</p>
                            )}
                            {skoleni.typ_zakonceni === 'kviz_kvalifikace' && skoleni.kvalifikace_dovednost && (
                                <p>Po úspěchu: posun dovednosti <strong style={{ color: '#3b82f6' }}>{skoleni.kvalifikace_dovednost.nazev}</strong> na úroveň {skoleni.kvalifikace_uroven}</p>
                            )}
                        </div>
                    </div>
                )}

                {/* Start button */}
                <button
                    onClick={startTraining}
                    disabled={starting}
                    className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-base font-semibold"
                    style={{ background: '#B47AEA', color: 'white', border: 'none', cursor: starting ? 'wait' : 'pointer' }}
                >
                    {starting ? <><Loader2 size={18} className="animate-spin" /> Spouštím...</> : <><Play size={18} /> Zahájit školení</>}
                </button>
            </div>
        );
    }

    const completeSekce = async () => {
        if (!aktivniSekce) return;
        const resp = await apiPost(`/api/moje-skoleni/${id}/sekce/${aktivniSekce.id}/dokoncit`);

        if (resp.stav === 'dokonceno') {
            setShowComplete(true);
            return;
        }

        if (isLastSekce && hasKviz) {
            setShowKviz(true);
        } else if (!isLastSekce) {
            setCurrentSekce(prev => prev + 1);
        }

        fetchData();
    };

    const submitKviz = async () => {
        setSubmittingKviz(true);
        const odpovedi = skoleni.otazky.map(q => ({
            otazka_id: q.id,
            vybrane: kvizOdpovedi[q.id] || [],
        }));

        const resp = await apiPost(`/api/moje-skoleni/${id}/kviz`, { odpovedi });
        setKvizVysledek(resp);
        setSubmittingKviz(false);

        if (resp.stav === 'dokonceno') {
            setShowComplete(true);
        }
    };

    const retryKviz = () => {
        setKvizVysledek(null);
        setKvizOdpovedi({});
    };

    const toggleOdpoved = (otazkaId, odpovedId, typ) => {
        setKvizOdpovedi(prev => {
            const current = prev[otazkaId] || [];
            if (typ === 'vyber_vice') {
                return { ...prev, [otazkaId]: current.includes(odpovedId) ? current.filter(id => id !== odpovedId) : [...current, odpovedId] };
            }
            // vyber_jedne or ano_ne
            return { ...prev, [otazkaId]: [odpovedId] };
        });
    };

    const formatTime = (s) => {
        if (!s) return '0m';
        const h = Math.floor(s / 3600);
        const m = Math.floor((s % 3600) / 60);
        if (h > 0) return `${h}h ${m}m`;
        return `${m}m`;
    };

    const previewMode = prubeh.stav === 'dokonceno';

    // ── Completed screen (zobrazí se jen při právě dokončeném školení v této session) ──
    if (showComplete) {
        const lastKviz = prubeh.kviz_vysledky?.[prubeh.kviz_vysledky.length - 1] || kvizVysledek?.vysledek;
        return (
            <div className="max-w-lg mx-auto text-center py-12 space-y-5">
                <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(16,185,129,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto' }}>
                    <CheckCircle2 size={32} style={{ color: '#10b981' }} />
                </div>
                <h2 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>Školení dokončeno!</h2>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{skoleni.nazev}</p>

                <div className="flex justify-center gap-6 text-sm" style={{ color: 'var(--text-muted)' }}>
                    <span className="flex items-center gap-1"><Clock size={14} /> {formatTime(prubeh.cas_celkem_s)}</span>
                    {lastKviz && <span className="flex items-center gap-1"><Award size={14} /> Kvíz: {lastKviz.uspesnost_pct}%</span>}
                </div>

                {skoleni.typ_zakonceni === 'kviz_certifikace' && (
                    <div className="rounded-lg p-3 mx-auto" style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)', maxWidth: 360 }}>
                        <Award size={18} style={{ color: '#10b981', margin: '0 auto 4px' }} />
                        <p className="text-sm font-medium" style={{ color: '#10b981' }}>
                            {skoleni.certifikace_typ_kod ? `Certifikát ${skoleni.certifikace_typ_kod} obnoven` : 'Certifikace dokončena'}
                        </p>
                        <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>Platnost {skoleni.certifikace_platnost_mesicu} měsíců</p>
                    </div>
                )}

                {skoleni.typ_zakonceni === 'kviz_kvalifikace' && skoleni.kvalifikace_dovednost && (
                    <div className="rounded-lg p-3 mx-auto" style={{ background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.2)', maxWidth: 360 }}>
                        <Award size={18} style={{ color: '#3b82f6', margin: '0 auto 4px' }} />
                        <p className="text-sm font-medium" style={{ color: '#3b82f6' }}>
                            {skoleni.kvalifikace_dovednost.kod} – {skoleni.kvalifikace_dovednost.nazev}
                        </p>
                        <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>Posun na úroveň {skoleni.kvalifikace_uroven}</p>
                    </div>
                )}

                <button
                    onClick={() => onNavigate('prehled', { tab: 'rozvoj' })}
                    className="px-6 py-2.5 rounded-lg text-sm font-semibold"
                    style={{ background: 'var(--brand)', color: '#1a1a2e', border: 'none', cursor: 'pointer' }}
                >
                    Zpět na osobní rozvoj
                </button>
            </div>
        );
    }

    // ── Quiz screen ───────────────────
    if (showKviz) {
        // ── PREVIEW MODE: Výsledky kvízu (read-only) ──
        if (previewMode) {
            const lastKviz = prubeh.kviz_vysledky?.[prubeh.kviz_vysledky.length - 1];
            const odpovediMap = {};
            if (lastKviz?.odpovedi_zam) {
                lastKviz.odpovedi_zam.forEach(oz => { odpovediMap[oz.otazka_id] = oz; });
            }

            return (
                <div className="space-y-5" style={{ maxWidth: 760, margin: '0 auto' }}>
                    <div className="flex items-center gap-3">
                        <button onClick={() => setShowKviz(false)} className="p-2 rounded-lg" style={{ color: 'var(--text-muted)', border: '1px solid var(--border-default)', background: 'transparent', cursor: 'pointer' }}>
                            <ArrowLeft size={16} />
                        </button>
                        <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>Výsledky kvízu — {skoleni.nazev}</h2>
                    </div>

                    {/* Score banner */}
                    {lastKviz && (
                        <div className="text-center py-6 rounded-lg" style={{
                            background: lastKviz.uspel ? 'rgba(16,185,129,0.08)' : 'rgba(248,113,113,0.08)',
                            border: `1px solid ${lastKviz.uspel ? 'rgba(16,185,129,0.2)' : 'rgba(248,113,113,0.2)'}`,
                        }}>
                            {lastKviz.uspel
                                ? <CheckCircle2 size={32} style={{ color: '#10b981', margin: '0 auto 8px' }} />
                                : <X size={32} style={{ color: '#f87171', margin: '0 auto 8px' }} />
                            }
                            <p className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{lastKviz.uspesnost_pct}%</p>
                            <p className="text-sm" style={{ color: lastKviz.uspel ? '#10b981' : '#f87171' }}>
                                {lastKviz.uspel ? 'Úspěšně absolvováno' : 'Neúspěšný pokus'}
                            </p>
                            <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                                Pokus č. {lastKviz.pokus} &middot; {lastKviz.celkem_bodu}/{lastKviz.max_bodu} bodů
                            </p>
                        </div>
                    )}

                    {/* Otázky s barevnými odpověďmi */}
                    {skoleni.otazky.map((q, qi) => {
                        const zamOdpoved = odpovediMap[q.id];
                        const vybraneSet = new Set(zamOdpoved?.vybrane_odpovedi || []);

                        return (
                            <div key={q.id} className="rounded-lg p-4" style={{ background: 'var(--surface-elevated)', border: '1px solid var(--border-default)' }}>
                                <div className="flex items-center justify-between mb-3">
                                    <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                                        <span style={{ color: 'var(--brand)', fontWeight: 700 }}>{qi + 1}.</span> {q.text_otazky}
                                    </p>
                                    {zamOdpoved && (
                                        zamOdpoved.je_spravna
                                            ? <CheckCircle2 size={16} style={{ color: '#10b981', flexShrink: 0 }} />
                                            : <XCircle size={16} style={{ color: '#f87171', flexShrink: 0 }} />
                                    )}
                                </div>
                                <div className="space-y-1.5">
                                    {q.odpovedi.map(o => {
                                        const isSelected = vybraneSet.has(o.id);
                                        const isCorrect = o.je_spravna;

                                        let bg, borderColor, textColor;
                                        if (isCorrect) {
                                            bg = 'rgba(16,185,129,0.12)';
                                            borderColor = 'rgba(16,185,129,0.4)';
                                            textColor = '#10b981';
                                        } else if (isSelected) {
                                            bg = 'rgba(248,113,113,0.12)';
                                            borderColor = 'rgba(248,113,113,0.4)';
                                            textColor = '#f87171';
                                        } else {
                                            bg = 'var(--surface-base)';
                                            borderColor = 'var(--border-default)';
                                            textColor = 'var(--text-secondary)';
                                        }

                                        return (
                                            <div
                                                key={o.id}
                                                className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm"
                                                style={{ background: bg, border: `1px solid ${borderColor}`, color: textColor }}
                                            >
                                                <div style={{
                                                    width: 18, height: 18, flexShrink: 0,
                                                    borderRadius: q.typ === 'vyber_vice' ? 4 : '50%',
                                                    border: `2px solid ${isCorrect ? '#10b981' : isSelected ? '#f87171' : 'var(--border-strong)'}`,
                                                    background: isSelected ? (isCorrect ? '#10b981' : '#f87171') : 'transparent',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                }}>
                                                    {isSelected && <Check size={10} color="white" strokeWidth={3} />}
                                                </div>
                                                {o.text}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}

                    {/* Zpět na obsah */}
                    <button
                        onClick={() => setShowKviz(false)}
                        className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold mx-auto"
                        style={{ background: 'var(--surface-elevated)', color: 'var(--text-secondary)', border: '1px solid var(--border-default)', cursor: 'pointer' }}
                    >
                        <ArrowLeft size={14} /> Zpět na obsah
                    </button>
                </div>
            );
        }

        // ── NORMÁLNÍ MÓD: Interaktivní kvíz ──
        return (
            <div className="space-y-5" style={{ maxWidth: 760, margin: '0 auto' }}>
                <div className="flex items-center gap-3">
                    <button onClick={() => setShowKviz(false)} className="p-2 rounded-lg" style={{ color: 'var(--text-muted)', border: '1px solid var(--border-default)', background: 'transparent', cursor: 'pointer' }}>
                        <ArrowLeft size={16} />
                    </button>
                    <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>Kvíz — {skoleni.nazev}</h2>
                </div>

                {kvizVysledek ? (
                    <div className="space-y-4">
                        <div className="text-center py-8 rounded-lg" style={{
                            background: kvizVysledek.uspel ? 'rgba(16,185,129,0.08)' : 'rgba(248,113,113,0.08)',
                            border: `1px solid ${kvizVysledek.uspel ? 'rgba(16,185,129,0.2)' : 'rgba(248,113,113,0.2)'}`,
                        }}>
                            {kvizVysledek.uspel ? <CheckCircle2 size={40} style={{ color: '#10b981', margin: '0 auto 8px' }} /> : <X size={40} style={{ color: '#f87171', margin: '0 auto 8px' }} />}
                            <h3 className="text-lg font-bold" style={{ color: kvizVysledek.uspel ? '#10b981' : '#f87171' }}>
                                {kvizVysledek.uspel ? 'Úspěšně absolvováno!' : 'Bohužel neúspěšně'}
                            </h3>
                            <p className="text-2xl font-bold mt-2" style={{ color: 'var(--text-primary)' }}>{kvizVysledek.uspesnost_pct}%</p>
                            <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                                Minimum: {skoleni.min_uspesnost_pct}%
                                {kvizVysledek.zbyvajici_pokusy !== null && ` · Zbývající pokusy: ${kvizVysledek.zbyvajici_pokusy}`}
                            </p>
                        </div>

                        {!kvizVysledek.uspel && kvizVysledek.zbyvajici_pokusy !== 0 && (
                            <button onClick={retryKviz} className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold mx-auto" style={{ background: 'var(--brand)', color: '#1a1a2e', border: 'none', cursor: 'pointer' }}>
                                <RotateCcw size={14} /> Zkusit znovu
                            </button>
                        )}

                        {(kvizVysledek.uspel || kvizVysledek.zbyvajici_pokusy === 0) && (
                            <button onClick={() => onNavigate('prehled', { tab: 'rozvoj' })} className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold mx-auto" style={{ background: 'var(--brand)', color: '#1a1a2e', border: 'none', cursor: 'pointer' }}>
                                Zpět na osobní rozvoj
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="space-y-4">
                        {skoleni.otazky.map((q, qi) => (
                            <div key={q.id} className="rounded-lg p-4" style={{ background: 'var(--surface-elevated)', border: '1px solid var(--border-default)' }}>
                                <p className="text-sm font-medium mb-3" style={{ color: 'var(--text-primary)' }}>
                                    <span style={{ color: 'var(--brand)', fontWeight: 700 }}>{qi + 1}.</span> {q.text_otazky}
                                </p>
                                <div className="space-y-1.5">
                                    {q.odpovedi.map(o => {
                                        const selected = (kvizOdpovedi[q.id] || []).includes(o.id);
                                        return (
                                            <button
                                                key={o.id}
                                                onClick={() => toggleOdpoved(q.id, o.id, q.typ)}
                                                className="w-full text-left flex items-center gap-3 px-3 py-2 rounded-lg text-sm"
                                                style={{
                                                    background: selected ? 'var(--brand-bg)' : 'var(--surface-base)',
                                                    border: `1px solid ${selected ? 'var(--brand)' : 'var(--border-default)'}`,
                                                    color: selected ? 'var(--brand-text)' : 'var(--text-secondary)',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.15s',
                                                }}
                                            >
                                                <div style={{
                                                    width: 18, height: 18, flexShrink: 0,
                                                    borderRadius: q.typ === 'vyber_vice' ? 4 : '50%',
                                                    border: `2px solid ${selected ? 'var(--brand)' : 'var(--border-strong)'}`,
                                                    background: selected ? 'var(--brand)' : 'transparent',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                }}>
                                                    {selected && <Check size={10} color="white" strokeWidth={3} />}
                                                </div>
                                                {o.text}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}

                        <button
                            onClick={submitKviz}
                            disabled={submittingKviz || Object.keys(kvizOdpovedi).length < skoleni.otazky.length}
                            className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-lg text-base font-semibold"
                            style={{
                                background: submittingKviz || Object.keys(kvizOdpovedi).length < skoleni.otazky.length ? 'var(--surface-elevated)' : 'var(--brand)',
                                color: submittingKviz || Object.keys(kvizOdpovedi).length < skoleni.otazky.length ? 'var(--text-muted)' : '#1a1a2e',
                                cursor: submittingKviz || Object.keys(kvizOdpovedi).length < skoleni.otazky.length ? 'not-allowed' : 'pointer',
                                border: 'none',
                            }}
                        >
                            {submittingKviz ? <><Loader2 size={16} className="animate-spin" /> Vyhodnocuji...</> : 'Odeslat kvíz'}
                        </button>
                    </div>
                )}
            </div>
        );
    }

    // ── Training content view ──────────
    return (
        <div className="space-y-4" style={{ maxWidth: 760, margin: '0 auto' }}>
            {/* Header */}
            <div className="flex items-center gap-3">
                <button onClick={() => onNavigate('prehled', { tab: 'rozvoj' })} className="p-2 rounded-lg" style={{ color: 'var(--text-muted)', border: '1px solid var(--border-default)', background: 'transparent', cursor: 'pointer' }}>
                    <ArrowLeft size={16} />
                </button>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <h1 className="text-lg font-bold truncate" style={{ color: 'var(--text-primary)' }}>{skoleni.nazev}</h1>
                        {previewMode && (
                            <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold shrink-0"
                                style={{ background: 'rgba(16,185,129,0.12)', color: '#10b981', border: '1px solid rgba(16,185,129,0.25)' }}>
                                <Eye size={10} /> Náhled
                            </span>
                        )}
                    </div>
                    <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                        Sekce {currentSekce + 1} / {sekce.length} &middot; <Clock size={10} className="inline" /> {formatTime(prubeh.cas_celkem_s)}
                    </p>
                </div>
            </div>

            {/* Stepper */}
            <div className="flex items-center gap-1" style={{ padding: '0 4px' }}>
                {sekce.map((s, i) => {
                    const done = previewMode || doneSekceIds.has(s.id);
                    const active = i === currentSekce;
                    return (
                        <div key={s.id} className="flex items-center" style={{ flex: i < sekce.length - 1 ? 1 : 'none' }}>
                            <button
                                onClick={() => setCurrentSekce(i)}
                                style={{
                                    width: active ? 28 : 22, height: active ? 28 : 22,
                                    borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: 10, fontWeight: 700, flexShrink: 0,
                                    transition: 'all 0.2s',
                                    background: active ? 'var(--brand)' : done ? '#10b981' : 'transparent',
                                    border: `2px solid ${active ? 'var(--brand)' : done ? '#10b981' : 'var(--border-strong)'}`,
                                    color: active ? '#1a1a2e' : done ? 'white' : 'var(--text-muted)',
                                    cursor: 'pointer',
                                    boxShadow: active ? '0 0 0 3px var(--brand-bg)' : 'none',
                                }}
                            >
                                {done ? <Check size={10} strokeWidth={3} /> : i + 1}
                            </button>
                            {i < sekce.length - 1 && (
                                <div style={{ flex: 1, height: 2, margin: '0 2px', borderRadius: 1, background: 'var(--border-default)', position: 'relative', overflow: 'hidden' }}>
                                    <div style={{ position: 'absolute', inset: 0, background: '#10b981', transform: `scaleX(${done ? 1 : 0})`, transformOrigin: 'left', transition: 'transform 0.3s', borderRadius: 1 }} />
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Section content */}
            {aktivniSekce && (
                <div className="rounded-lg" style={{ border: '1px solid var(--border-default)', overflow: 'hidden' }}>
                    <div className="px-4 py-3 flex items-center gap-2" style={{ background: 'var(--surface-elevated)', borderBottom: '1px solid var(--border-default)' }}>
                        {(() => { const SIcon = SEKCE_TYP_ICONS[aktivniSekce.typ] || FileText; return <SIcon size={14} style={{ color: 'var(--brand)' }} />; })()}
                        <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{aktivniSekce.nazev}</span>
                    </div>

                    <div className="p-5" style={{ background: 'var(--surface-base)' }}>
                        {aktivniSekce.typ === 'obsah' && aktivniSekce.obsah_html && (
                            <div
                                className="tiptap-content"
                                style={{ color: 'var(--text-primary)', fontSize: 14, lineHeight: 1.7 }}
                                dangerouslySetInnerHTML={{ __html: aktivniSekce.obsah_html }}
                            />
                        )}

                        {aktivniSekce.typ === 'video' && (
                            <div className="space-y-3">
                                {aktivniSekce.popis && (
                                    <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{aktivniSekce.popis}</p>
                                )}
                                {aktivniSekce.video_url && (() => {
                                    const url = aktivniSekce.video_url;
                                    let embedUrl = url;
                                    const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/);
                                    if (ytMatch) embedUrl = `https://www.youtube.com/embed/${ytMatch[1]}`;
                                    const vmMatch = url.match(/vimeo\.com\/(\d+)/);
                                    if (vmMatch) embedUrl = `https://player.vimeo.com/video/${vmMatch[1]}`;

                                    return (
                                        <>
                                            <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, borderRadius: 6, overflow: 'hidden' }}>
                                                <iframe src={embedUrl} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }} allowFullScreen />
                                            </div>
                                            {!previewMode && !videoWatched[aktivniSekce.id] && (
                                                <button
                                                    onClick={() => setVideoWatched(prev => ({ ...prev, [aktivniSekce.id]: true }))}
                                                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium mx-auto"
                                                    style={{ background: 'rgba(180,122,234,0.12)', color: '#B47AEA', border: '1px solid rgba(180,122,234,0.25)', cursor: 'pointer' }}
                                                >
                                                    <Check size={12} /> Potvrzuji shlédnutí videa
                                                </button>
                                            )}
                                        </>
                                    );
                                })()}
                                {aktivniSekce.video_cesta && !aktivniSekce.video_url && (
                                    <video
                                        controls
                                        style={{ width: '100%', borderRadius: 6 }}
                                        src={`/storage/${aktivniSekce.video_cesta}`}
                                        onEnded={() => setVideoWatched(prev => ({ ...prev, [aktivniSekce.id]: true }))}
                                    />
                                )}
                                {!previewMode && !videoWatched[aktivniSekce.id] && (
                                    <p className="text-xs text-center" style={{ color: 'var(--text-muted)' }}>
                                        {aktivniSekce.video_url ? 'Potvrďte shlédnutí videa pro pokračování' : 'Přehrajte video do konce pro pokračování'}
                                    </p>
                                )}
                            </div>
                        )}

                        {aktivniSekce.typ === 'soubor' && aktivniSekce.soubor_nazev && (
                            <div className="space-y-3">
                                {aktivniSekce.popis && (
                                    <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{aktivniSekce.popis}</p>
                                )}
                                <PdfViewer
                                    url={`/storage/${aktivniSekce.soubor_cesta}`}
                                    nazev={aktivniSekce.soubor_nazev}
                                    onLastPage={() => setPdfRead(prev => ({ ...prev, [aktivniSekce.id]: true }))}
                                />
                                {!previewMode && !pdfRead[aktivniSekce.id] && (
                                    <p className="text-xs text-center" style={{ color: 'var(--text-muted)' }}>
                                        Přečtěte dokument do konce pro pokračování
                                    </p>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Navigation */}
            <div className="flex items-center justify-between">
                <button
                    onClick={() => setCurrentSekce(prev => Math.max(0, prev - 1))}
                    disabled={currentSekce === 0}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium"
                    style={{
                        color: currentSekce === 0 ? 'var(--text-muted)' : 'var(--text-secondary)',
                        cursor: currentSekce === 0 ? 'not-allowed' : 'pointer',
                        background: 'transparent',
                        border: '1px solid var(--border-default)',
                        opacity: currentSekce === 0 ? 0.5 : 1,
                    }}
                >
                    <ChevronLeft size={14} /> Zpět
                </button>

                {previewMode ? (
                    isLastSekce && hasKviz ? (
                        <button
                            onClick={() => setShowKviz(true)}
                            className="flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm font-semibold"
                            style={{ background: 'var(--brand)', color: '#1a1a2e', cursor: 'pointer', border: 'none' }}
                        >
                            Zobrazit výsledky kvízu
                        </button>
                    ) : !isLastSekce ? (
                        <button
                            onClick={() => setCurrentSekce(prev => prev + 1)}
                            className="flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm font-semibold"
                            style={{ background: 'var(--brand)', color: '#1a1a2e', cursor: 'pointer', border: 'none' }}
                        >
                            Další <ChevronRight size={14} />
                        </button>
                    ) : null
                ) : (() => {
                    const pdfBlocked = aktivniSekce?.typ === 'soubor' && aktivniSekce?.soubor_cesta && !pdfRead[aktivniSekce.id];
                    const videoBlocked = aktivniSekce?.typ === 'video' && (aktivniSekce?.video_url || aktivniSekce?.video_cesta) && !videoWatched[aktivniSekce.id];
                    const blocked = pdfBlocked || videoBlocked;
                    return (
                        <button
                            onClick={completeSekce}
                            disabled={blocked}
                            className="flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm font-semibold"
                            style={{
                                background: blocked ? 'var(--surface-elevated)' : 'var(--brand)',
                                color: blocked ? 'var(--text-muted)' : '#1a1a2e',
                                cursor: blocked ? 'not-allowed' : 'pointer',
                                border: blocked ? '1px solid var(--border-default)' : 'none',
                                opacity: blocked ? 0.5 : 1,
                            }}
                        >
                            {isLastSekce ? (hasKviz ? 'Pokračovat na kvíz' : 'Dokončit školení') : 'Další'}
                            {!isLastSekce && <ChevronRight size={14} />}
                        </button>
                    );
                })()}
            </div>
        </div>
    );
}
